import type { OverflowPoolInfo } from '@/types/api'

interface GlobalOverflowBarProps {
  pools: OverflowPoolInfo[]
}

function avgPctUsed(pools: OverflowPoolInfo[]): number {
  if (pools.length === 0) return 0
  return Math.round(pools.reduce((s, p) => s + p.pct_used, 0) / pools.length)
}

/**
 * Aggregate fill — percentages only, no $ (margin-safe).
 *
 * `pct_used` is now delivery against the advertiser cap, not the pool's draw ratio,
 * so this bar tracks how full the offer actually is. See cap-api
 * `services/geo-fill.ts` for why the two numbers diverge.
 */
export function GlobalOverflowBar({ pools }: GlobalOverflowBarProps) {
  const pct = avgPctUsed(pools)
  const anyCapacity = pools.some((p) => p.has_capacity && !p.is_locked)

  let barColor = 'bg-brand-blue'
  if (pct > 90) barColor = 'bg-brand-red'
  else if (pct > 70) barColor = 'bg-brand-orange'

  return (
    <div className="bg-surface-card border border-gray-200 rounded-2xl p-4 shadow-xs">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">Overflow pool</h3>
          <p className="text-xs text-text-muted mt-0.5">
            {pools.length} geo{pools.length !== 1 ? 's' : ''} you can draw from
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-text-primary">
            {anyCapacity ? 'Capacity available' : 'Cap reached or locked'}
          </p>
          <p className="text-xs text-text-muted">Avg cap filled {pct}%</p>
        </div>
      </div>

      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  )
}
